import { inject, Injectable } from '@angular/core';
import { type Observable, firstValueFrom } from 'rxjs';

import type { RemicShelf } from '../v2/models/remic-shelf.model';
import type { ModelingVersion } from '../v2/models/model-engine.model';
import { CdxApiGatewayHttpService } from '../../shared/services/cdx-api-gateway-http.service';
import { Endpoints } from './endpoints';
import { IndicateLoading } from '../../shared/services/fm-loading-page.service';
import { environment } from '../../../environments/environment';
import { LoggingService } from '../../common/services/logging.service';

export interface ModelVersions {
  models: ModelingVersion[];
}

@Injectable({
  providedIn: 'root',
})
export class RemicShelfService {
  private readonly http = inject(CdxApiGatewayHttpService);
  private readonly loggingService = inject(LoggingService);

  public settlementDate(settlementDateRequest: any): Observable<object> {
    return this.http.post(
      Endpoints.REMIC_SETTLE_DATE_INFO_ENDPOINT,
      JSON.stringify(settlementDateRequest),
    );
  }

  public getReviewSchedule(reviewScheduleReq: any): Observable<object> {
    return this.http.post(
      Endpoints.REMIC_REVIEW_SCHEDULE_ENDPOINT,
      JSON.stringify(reviewScheduleReq),
    );
  }

  public remicSave(remicSaveReq: any): Observable<object> {
    return this.http.post(Endpoints.REMIC_SAVE_ENDPOINT, JSON.stringify(remicSaveReq));
  }

  public listSettledREMICs(): Observable<{
    remicSettledDetails: RemicShelf[];
  }> {
    return this.http.post(Endpoints.REMIC_LIST_SETTLED_ENDPOINT, JSON.stringify({}));
  }

  public commitListedREMICs(): Observable<{
    remicCommittedDetails: RemicShelf[];
  }> {
    return this.http.post(Endpoints.REMIC_LIST_COMMITTED_ENDPOINT, JSON.stringify({}));
  }

  public upfUploadLocation(request: any): Observable<object> {
    return this.http.post(Endpoints.UPF_UPLOAD_LOCATION_ENDPOINT, JSON.stringify(request));
  }

  public upfUploadRemic(formData: any, options: any, url: string): Observable<object> {
    return this.http.put(url, formData, options);
  }

  public upfUploadStatus(request: any): Observable<object> {
    return this.http.post(Endpoints.UPF_UPLOAD_STATUS_ENDPOINT, JSON.stringify(request));
  }

  public upfRemicUploadStatus(request: any): Observable<object> {
    return this.http.post(Endpoints.UPF_REMIC_UPLOAD_STATUS_ENDPOINT, JSON.stringify(request));
  }

  public remicTransactionUpdate(request: any): Observable<object> {
    return this.http.post(Endpoints.REMIC_TRANSACTION_ENDPOINT, JSON.stringify(request));
  }

  @IndicateLoading
  public getModelEngineVersions(deal: string): Observable<ModelVersions> {
    return this.http.get(Endpoints.MODEL_ENGINE + 'deal/cash-flow-models?dealId=' + deal);
  }

  @IndicateLoading
  public getInputFiles(dealId: string, modelVersion: string): Observable<object> {
    return this.http.get(
      Endpoints.GET_INPUT_FILES + 'dealId=' + dealId + '&modelId=' + modelVersion,
    );
  }

  @IndicateLoading
  public getOutputFiles(dealId: string, modelVersion: string): Observable<object> {
    return this.http.get(
      Endpoints.GET_OUTPUT_FILES + 'dealId=' + dealId + '&modelId=' + modelVersion,
    );
  }

  @IndicateLoading
  public async downloadEmptyTemplate(): Promise<void> {
    try {
      // Get the presigned download URL
      const url = `${environment.dealSubmissionBaseUrl}deal/templatexlspresigneddownloadurl`;
      const response = (await firstValueFrom(this.http.get(url))) as {
        fileName: string;
        url: string;
      };

      // Download the file directly using the presigned URL (no CORS issues)
      const link = document.createElement('a');
      link.href = response.url;
      link.download = response.fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      this.loggingService.error('Error downloading empty template', error);
      throw error;
    }
  }
}
